/* ──────────────────────────────────────────────────────────────
 *  useComputeExecution — Orchestrates the compute/predict flow
 *
 *  CHANGES:
 *   - Geometry loop now delegates to generateGeometryWithProgress
 *     (src/utils/geometryGeneration.ts), the same helper
 *     useSampleSelection uses when the user switches samples from
 *     the search bar, graph, or Explore Designs dialog. Keeps
 *     progress reporting and geometryReady bookkeeping identical
 *     across both flows instead of duplicating the loop.
 * ────────────────────────────────────────────────────────────── */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { computeGen } from '@/api/computeApi';
import type { ComputeGenPayload } from '@/api/computeApi';
import { useInputStore } from '@/stores/useInputStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { useConfigStore } from '@/stores/useConfigStore';
import { useSampleStore } from '@/stores/useSampleStore';
import { useUIStore } from '@/stores/useUIStore';
import { useJobStore } from '@/stores/useJobStore';
import { API_BUSHING_TYPE_MAP } from '@/constants/bushingTypes';
import type { BushingType } from '@/types/bushing';
import type { GenRow } from '@/types/sample';
import { generateGeometryWithProgress } from '@/utils/geometryGeneration';
import { geometryKeys } from '@/hooks/useGeometry';

export interface UseComputeExecutionReturn {
  computing: boolean;
  doCompute: () => Promise<void>;
}

export function useComputeExecution(): UseComputeExecutionReturn {
  const [computing, setComputing] = useState(false);
  const showLoading = useUIStore((s) => s.showLoading);
  const hideLoading = useUIStore((s) => s.hideLoading);
  const markClean = useInputStore((s) => s.markClean);
  const queryClient = useQueryClient();
  const setComputeJobId = useJobStore((s) => s.setComputeJobId);

  const doCompute = useCallback(async () => {
    setComputing(true);
    showLoading('Preparing request…');

    // Clear existing geometry immediately so old preview goes away
    useSampleStore.setState({ currentSelectedId: null });
    setComputeJobId(null);
    queryClient.removeQueries({ queryKey: geometryKeys.all });

    try {
      const {
        packaging,
        target,
        sleeveThickness,
        shoreA,
        calculatedMinContactArea,
      } = useInputStore.getState();

      const currentBushingType = useBushingStore.getState().bushingType;

      const {
        stiffnessPriorities: priorities,
        coreMaterial,
        outerMaterial,
        interSleeveMaterial,
        weightPockets,
        minContactArea,
      } = useConfigStore.getState();

      const apiBushingType =
        API_BUSHING_TYPE_MAP[currentBushingType as BushingType] ??
        currentBushingType;

      useUIStore.getState().updateProgress(3, 'Preparing request…');

      const payload: ComputeGenPayload = {
        sleeve_length: packaging.outerSleeveLength!,
        sleeve_thk: sleeveThickness,
        core_length: packaging.coreLength!,
        control_arm_id: packaging.controlArmId!,
        core_od_min: packaging.coreOdMin!,
        core_od_max: packaging.coreOdMax!,
        Cx_target: target.targets['Cx'] ?? null,
        Cz_target: target.targets['Cz'] ?? null,
        Torsion_target: target.targets['Torsion'] ?? null,
        Conical_target: target.targets['Conical'] ?? null,
        shore_a: shoreA,
      };

      if (currentBushingType === 'C1') {
        if (packaging.collarOd != null) {
          payload.collar_od = packaging.collarOd;
        }
        if (packaging.h != null) {
          payload.h = packaging.h;
        }

        const ltValue = target.linearTravel['axial_c1'];
        if (ltValue != null) {
          payload.linear_travel = ltValue;
        }
      }

      if (
        currentBushingType === 'D1' ||
        currentBushingType === 'BYD EV Voided' ||
        currentBushingType === 'BYD EV Solid'
      ) {
        payload.Cy_target = target.targets['Cy'] ?? null;
      }

      const hasNonZeroPriority = Object.values(priorities).some(
        (v) => (v as number) > 0,
      );

      if (hasNonZeroPriority) {
        payload.stiffness_priorities = priorities;
      }

      useUIStore.getState().updateProgress(5, 'Sending request to model…');

      const response = await computeGen(
        apiBushingType as BushingType,
        payload,
        (pct, msg) => useUIStore.getState().updateProgress(pct, msg),
      );

      useUIStore.getState().updateProgress(48, 'Samples received…');

      const genRows: GenRow[] = response.samples.map((sample, index) => {
        const id =
          (sample['sample_id'] as string) ??
          `N${String(index + 1).padStart(4, '0')}`;

        const { sample_id: _removed, ...rest } = sample;

        return {
          ...rest,
          id,
          is_new: true,
        } as GenRow;
      });

      useUIStore.getState().updateProgress(52, 'Selecting best design…');

      const newComputeJobId = crypto.randomUUID();

      const bestId = response.best_sample_id ?? null;
      const { originalSamples } = useSampleStore.getState();

      const genRowsById = Object.fromEntries(
        genRows.map((r) => [r.id, r]),
      ) as Record<string, GenRow>;

      useUIStore.getState().updateProgress(55, 'Updating design store…');

      const geometryConfigSnapshot = {
        coreMaterial,
        outerMaterial,
        interSleeveMaterial,
        weightPockets,
        minContactArea,
        calculatedMinContactArea,
        coreId: packaging.coreId,
        sleeveThickness,
        linearTravelRadialPlus: target.linearTravel?.radial_plus ?? null,
        linearTravelRadialMinus: target.linearTravel?.radial_minus ?? null,
      };

      useSampleStore.setState({
        generatedSamples: genRows,
        allSamples: [...originalSamples, ...genRows],
        genRowsById,
        snapshotTargets: { ...target.targets },
        bestSampleId: bestId,
        currentSelectedId: bestId,
        currentRating: 0,
        geometryConfigSnapshot,
      });

      // ── Generate geometry for best sample via the shared helper ──
      let geometrySucceeded = false;

      if (bestId && genRowsById[bestId]) {
        useUIStore.getState().updateProgress(60, 'Generating geometry…');

        const rawSampleData = genRowsById[bestId] as unknown as Record<string, number>;

        const effectiveMinArea =
          weightPockets && coreMaterial !== 'Steel'
            ? minContactArea ?? calculatedMinContactArea ?? 0
            : 0;

        geometrySucceeded = await generateGeometryWithProgress({
          queryClient,
          jobId: newComputeJobId,
          sampleId: bestId,
          bushingType: currentBushingType,
          rawSampleData,
          coreMaterial,
          outerMaterial,
          interSleeveMaterial,
          coreId: packaging.coreId ?? null,
          effectiveMinArea,
          weightPockets,
          sleeveThickness,
          linearTravelRadialPlus: target.linearTravel?.radial_plus ?? null,
          linearTravelRadialMinus: target.linearTravel?.radial_minus ?? null,
          progressStart: 60,
          progressEnd: 90,
        });
      }

      // ── Publish the job ID ONLY after geometry succeeded ──────────
      if (geometrySucceeded) {
        setComputeJobId(newComputeJobId);
      } else {
        useUIStore
          .getState()
          .openConstraintDialog(
            'Geometry could not be generated for the selected design. Export is unavailable until geometry succeeds.',
          );
      }

      useUIStore.getState().updateProgress(92, 'Finalizing…');

      useUIStore.getState().resetExploreState();
      useConfigStore.getState().saveSnapshot();

      markClean();
      useUIStore.getState().updateProgress(100, 'Preview ready');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Prediction failed';
      useUIStore.getState().openConstraintDialog(message);
    } finally {
      setComputing(false);
      hideLoading();
    }
  }, [showLoading, hideLoading, markClean, queryClient, setComputeJobId]);

  return { computing, doCompute };
}